import * as React from 'react';

import { Button, Form, Input, Progress } from 'antd';
import { PASSWORD_REGEX } from '../../helpers';
import { checkPasswordStrength, getProgressColor } from '../../helpers/handleEntropyPassword';

interface ChangePasswordProps {
    hideOldPassword?: boolean;
    title?: string | undefined;
    handleChangePassword: (payload: { old_password?: string; new_password: string; confirm_password: string }) => void;
}

const ChangePasswordComponent: React.FC<ChangePasswordProps> = (props) => {
    const [passwordStrength, setPasswordStrength] = React.useState<number>(0);
    const handlePasswordStrength = (password: string) => {
        const strength = checkPasswordStrength(password);
        setPasswordStrength(strength);
    };

    const handleChangePassword = (values: any) => {
        const payload = props.hideOldPassword
            ? {
                  new_password: values.newPassword,
                  confirm_password: values.confirmPassword,
              }
            : {
                  old_password: values.oldPassword,
                  new_password: values.newPassword,
                  confirm_password: values.confirmPassword,
              };

        props.handleChangePassword(payload);
    };

    const isValidForm = (oldPassword: string, newPassword: string, confirmPassword: string) => {
        const isNewPasswordValid = newPassword.match(PASSWORD_REGEX);
        const isConfirmPasswordValid = newPassword === confirmPassword;
        const isOldPasswordValid = (!props.hideOldPassword && oldPassword) || true;

        return isOldPasswordValid && isNewPasswordValid && isConfirmPasswordValid;
    };

    return (
        <div className="pg-change-password">
            {props.title && <div className="pg-change-password__header">{props.title}</div>}

            <div className="pg-change-password__wrapper">
                <Form onFinish={handleChangePassword} layout="vertical">
                    {!props.hideOldPassword && (
                        <Form.Item
                            name="oldPassword"
                            rules={[{ required: true, message: 'Please input your old password!' }]}>
                            <Input.Password
                                placeholder="Old password"
                                size="large"
                                style={{
                                    backgroundColor: 'var(--main-background-color)',
                                    color: 'var(--primary-text-color-main)',
                                }}
                            />
                        </Form.Item>
                    )}
                    <div className="cr-sign-up-form__form-content___password-wrapper">
                        <Form.Item
                            name="newPassword"
                            rules={[{ required: true, message: 'Please input your new password!' }]}>
                            <Input.Password
                                placeholder="New password"
                                size="large"
                                onChange={(e) => handlePasswordStrength(e.target.value)}
                                style={{
                                    backgroundColor: 'var(--main-background-color)',
                                    color: 'var(--primary-text-color-main)',
                                }}
                            />
                        </Form.Item>
                        <div className="password-progress">
                            <div
                                style={{
                                    color:
                                        passwordStrength === 1
                                            ? 'red'
                                            : passwordStrength === 2
                                              ? 'orange'
                                              : passwordStrength === 3
                                                ? 'blue'
                                                : passwordStrength === 4
                                                  ? 'green'
                                                  : 'inherit',
                                }}>
                                {passwordStrength === 1 && 'Weak'}
                                {passwordStrength === 2 && 'Fair'}
                                {passwordStrength === 3 && 'Good'}
                                {passwordStrength === 4 && 'Strong'}
                            </div>
                            <Progress
                                percent={(passwordStrength / 4) * 100}
                                showInfo={false}
                                strokeColor={getProgressColor(passwordStrength)}
                            />
                        </div>
                    </div>

                    <Form.Item
                        name="confirmPassword"
                        dependencies={['newPassword']}
                        hasFeedback
                        rules={[
                            { required: true, message: 'Please confirm your new password!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('The two passwords do not match!'));
                                },
                            }),
                        ]}>
                        <Input.Password
                            placeholder="Confirm password"
                            size="large"
                            style={{
                                backgroundColor: 'var(--main-background-color)',
                                color: 'var(--primary-text-color-main)',
                            }}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            className="pg-change-password__wrapper___button-submit"
                            block
                            type="primary"
                            htmlType="submit"
                            size="large"
                            disabled={!isValidForm}>
                            Change Password
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </div>
    );
};

export const ChangePassword = React.memo(ChangePasswordComponent);
